import { useMemo, useState } from "react";
import { BuzerIcon } from "../../BuzerIcons";
import { LIFE_CATS } from "../../lib/constants";
import { EnneagramChart } from "../../components/EnneagramChart";
import { Sheet } from "../../components/ui/ds";

// ─────────────────────────────────────────────────────────────
//  v33d — „kvetina" (EnneagramChart) naplnená REÁLNYMI dátami: rozloženie návykov
//  podľa životných kategórií (Zdravie/Myseľ/Duša/Vzťahy/Práca/Financie/Domov/Zábava).
//  Posledná dlaždica v mriežke Štatistiky. Dĺžka lupeňa = relatívny počet návykov v
//  kategórii (normované na najväčšiu), farby = paleta --enn-1…9 (v poradí lupeňov).
//  Enneagram „center s číslom typu" tu netreba — dominantnú kategóriu ukazuje hlavička.
// ─────────────────────────────────────────────────────────────

const ENN_COLORS = ["var(--enn-1)", "var(--enn-2)", "var(--enn-3)", "var(--enn-4)", "var(--enn-5)", "var(--enn-6)", "var(--enn-7)", "var(--enn-8)", "var(--enn-9)"];
const plural = (n) => (n === 1 ? "návyk" : n >= 2 && n <= 4 ? "návyky" : "návykov");

export function EnneagramTile({ habits = [], competitions = [], style }) {
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(null);  // v33h — zvýraznený lupeň/riadok v detaile (klik)

  // aktívne životné kategórie, v pevnom poradí LIFE_CATS. Počítame VŠETKO, čo má životnú kategóriu:
  // bežné návyky (h.category) + zapojené výzvy (návykové výzvy sú tiež v habits) + súťaže
  // (samostatné, bez návyku — berieme comp.life_cat).
  const cats = useMemo(() => {
    const counts = {};
    for (const h of habits) { const k = h.category; if (k) counts[k] = (counts[k] || 0) + 1; }
    for (const c of competitions) { const k = c.life_cat; if (k) counts[k] = (counts[k] || 0) + 1; }
    return LIFE_CATS.map((c) => ({ key: c.key, label: c.label, icon: c.icon, count: counts[c.key] || 0 }))
      .filter((r) => r.count > 0);
  }, [habits, competitions]);

  // v33h — žiadne návyky v kategóriách: namiesto skrytia ukáž pozvánku (nech vie, načo to je).
  if (cats.length === 0) {
    return (
      <div style={{ gridColumn: "1 / -1", background: "var(--neutral-surface)", border: "1px solid var(--neutral-border)",
        borderRadius: "var(--radius-lg)", boxShadow: "var(--elev-1)", padding: "16px 16px 18px", ...style }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
          <span style={{ display: "inline-flex", color: "var(--neutral-text-main)" }}><BuzerIcon name="flower" size={18} color="currentColor" /></span>
          <div style={{ fontFamily: "var(--ff-mono)", fontWeight: 600, fontSize: 10.5, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--neutral-text-muted)" }}>Podľa kategórií</div>
        </div>
        <div style={{ fontSize: 13, lineHeight: 1.5, color: "var(--neutral-text-subtle)" }}>
          Zatiaľ nemáš návyky zaradené do kategórií. Keď k návykom priradíš kategóriu (Zdravie, Myseľ, Duša, Vzťahy, Práca, Financie, Domov, Zábava), uvidíš tu rozloženie — čomu sa v živote najviac venuješ.
        </div>
      </div>
    );
  }

  const total = cats.reduce((s, c) => s + c.count, 0);
  const maxCount = Math.max(...cats.map((c) => c.count));
  // dĺžka lupeňa (0–100) = podiel voči najväčšej kategórii → plná, pekná kvetina
  const values = cats.map((c) => Math.round((100 * c.count) / maxCount));
  const labels = cats.map((c) => c.label);
  const dom = cats.reduce((best, c, i) => (c.count > cats[best].count ? i : best), 0);
  // medzera medzi lupeňmi úmerná počtu kategórií (menej kategórií = širší slot → väčšia medzera),
  // ale zmenšená o ~40 % oproti pôvodnej (8–18°→5–11°, faktor 0.24→0.144): lupene majú takmer
  // splývať do jedného sochárskeho kvetu (nie oddelené kvapky), medzera len naznačí okvetný lístok.
  const gap = Math.min(11, Math.max(5, Math.round((360 / cats.length) * 0.144)));

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} title="Návyky podľa kategórií — otvoriť detail"
        style={{ gridColumn: "1 / -1", textAlign: "left", cursor: "pointer", font: "inherit", color: "inherit",
          background: "var(--neutral-surface)", border: "1px solid var(--neutral-border)", borderRadius: "var(--radius-lg)",
          boxShadow: "var(--elev-1)", padding: "16px 16px 18px", WebkitTextFillColor: "currentColor", ...style }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
          <span style={{ display: "inline-flex", color: "var(--neutral-text-main)" }}><BuzerIcon name="flower" size={18} color="currentColor" /></span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: "var(--ff-mono)", fontWeight: 600, fontSize: 10.5, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--neutral-text-muted)" }}>Podľa kategórií</div>
            <div style={{ fontSize: 13, color: "var(--neutral-text-subtle)", marginTop: 2 }}>
              najviac: <strong style={{ color: "var(--neutral-text-main)" }}>{cats[dom].label}</strong> · {total} {plural(total)}
            </div>
          </div>
          <span style={{ display: "inline-flex", color: "var(--neutral-text-muted)" }}><BuzerIcon name="chevron" size={18} color="currentColor" /></span>
        </div>
        <EnneagramChart values={values} labels={labels} gap={gap} showCenter={false} animated />
      </button>
      {open && (
        <Sheet title="Návyky podľa kategórií" subtitle="Rozloženie tvojich návykov" icon="flower" onClose={() => { setOpen(false); setSel(null); }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12, paddingBottom: 4 }}>
            <div style={{ textAlign: "center", padding: "4px 4px 8px" }}>
              <EnneagramChart values={values} labels={labels} gap={gap} showCenter={false} size={260}
                selected={sel} onSelect={(i) => setSel((p) => (p === i ? null : i))} />
            </div>
            {/* legenda: farba lupeňa · kategória · počet návykov (v poradí lupeňov).
                v33h — klik na lupeň (alebo riadok) zvýrazní príslušný riadok kategórie. */}
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {cats.map((c, i) => {
                const on = sel === i;
                return (
                  <button key={c.key} type="button" onClick={() => setSel((p) => (p === i ? null : i))}
                    style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", width: "100%", textAlign: "left", cursor: "pointer", font: "inherit", WebkitTextFillColor: "currentColor",
                      background: on ? "var(--brand-primary-surface)" : "var(--surface-solid)",
                      border: on ? "1px solid var(--brand-primary-border)" : "1px solid var(--border)",
                      borderRadius: "var(--radius-lg)", transition: "background .15s, border-color .15s",
                      opacity: sel !== null && !on ? 0.55 : 1 }}>
                    <span style={{ width: 12, height: 12, borderRadius: "50%", flexShrink: 0, background: ENN_COLORS[i % ENN_COLORS.length] }} />
                    <span style={{ display: "inline-flex", color: "var(--neutral-text-muted)", flexShrink: 0 }}><BuzerIcon name={c.icon} size={16} color="currentColor" /></span>
                    <span style={{ flex: 1, minWidth: 0, fontWeight: 700, fontSize: 14, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.label}</span>
                    <span style={{ fontFamily: "var(--ff-ui)", fontSize: 12.5, color: "var(--neutral-text-muted)", flexShrink: 0 }}>{c.count} {plural(c.count)} · {Math.round((100 * c.count) / total)} %</span>
                  </button>
                );
              })}
            </div>
          </div>
        </Sheet>
      )}
    </>
  );
}
